import { useState, useEffect } from "react";
import useSWR from 'swr';
import TabPageLayout, { PageLoader, DataCard, EmptyState } from "./TabPageLayout";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import OnOffToggle from "./OnOffToggle";
import { 
  Tag, Percent, DollarSign, Plus, Trash2, Edit, Copy, Check, 
  Loader2, TrendingUp, ShoppingCart, Calendar, Download
} from "lucide-react";
import { toast } from "sonner";
import apiV2, { getOrgId } from "../utils/apiV2";

const MAX_CODES = 50;

const DiscountsTab = () => {
  const orgId = getOrgId();
  const [showModal, setShowModal] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  // Export discount codes to CSV
  const handleExport = () => {
    if (codes.length === 0) {
      toast.error('No codes to export');
      return;
    }

    // Build CSV
    const headers = ['Code', 'Description', 'Type', 'Value', 'Uses', 'Max Uses', 'Revenue Generated', 'Status', 'Expires'];
    const rows = codes.map(code => [
      code.code,
      code.description || '',
      code.discountType === 'percentage' ? 'Percentage' : 'Fixed Amount',
      code.discountType === 'percentage' ? `${code.discountValue}%` : `$${code.discountValue}`,
      code.currentUses || 0,
      code.maxUses || 'Unlimited',
      `$${(code.totalRevenueGenerated || 0).toFixed(2)}`,
      code.isActive ? 'Active' : 'Inactive',
      code.validUntil ? new Date(code.validUntil).toLocaleDateString() : 'Never'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `discount-codes-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success('Exported discount codes');
  };
  
  // Form state
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    discountType: 'percentage',
    discountValue: '',
    maxUses: '',
    expiresInDays: ''
  });

  // SWR for promo codes - filter to only discount codes (not affiliates)
  const { data, error, isLoading, mutate } = useSWR(
    orgId ? `/organizations/${orgId}/promo-codes?codeType=discount` : null,
    async (url) => {
      const res = await apiV2.get(url);
      return res.data;
    },
    { revalidateOnFocus: true, dedupingInterval: 30000 }
  );

  const codes = data?.promoCodes || [];
  const summary = data?.summary || {};

  // Calculate stats
  const totalCodes = codes.length;
  const activeCodes = codes.filter(c => c.isActive).length;
  const totalUses = codes.reduce((sum, c) => sum + (c.currentUses || 0), 0);
  const totalRevenue = codes.reduce((sum, c) => sum + (c.totalRevenueGenerated || 0), 0);

  const resetForm = () => {
    setFormData({
      code: '',
      description: '',
      discountType: 'percentage',
      discountValue: '',
      maxUses: '',
      expiresInDays: ''
    });
    setEditingCode(null);
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (code) => {
    setEditingCode(code);
    setFormData({
      code: code.code,
      description: code.description || '',
      discountType: code.discountType,
      discountValue: code.discountValue?.toString() || '',
      maxUses: code.maxUses?.toString() || '',
      expiresInDays: ''
    });
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.code || !formData.discountValue) {
      toast.error('Code and discount value are required');
      return;
    }

    if (!editingCode && codes.length >= MAX_CODES) {
      toast.error(`Maximum ${MAX_CODES} codes allowed`);
      return;
    }

    setSaving(true);
    try {
      let expirationDate = null;
      if (formData.expiresInDays) {
        const expDate = new Date();
        expDate.setDate(expDate.getDate() + parseInt(formData.expiresInDays));
        expirationDate = expDate.toISOString();
      }

      const payload = {
        code: formData.code.toUpperCase(),
        description: formData.description || null,
        discountType: formData.discountType,
        discountValue: parseFloat(formData.discountValue),
        maxUses: formData.maxUses ? parseInt(formData.maxUses) : null,
        validUntil: expirationDate,
        isAffiliate: false // Always false - no affiliates
      };

      if (editingCode) {
        await apiV2.patch(`/organizations/${orgId}/promo-codes/${editingCode.id}`, payload);
        toast.success('Discount code updated');
      } else {
        await apiV2.post(`/organizations/${orgId}/promo-codes`, payload);
        toast.success('Discount code created');
      }
      
      setShowModal(false);
      resetForm();
      mutate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save code');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (code) => {
    setTogglingId(code.id);
    try {
      await apiV2.patch(`/organizations/${orgId}/promo-codes/${code.id}`, {
        isActive: !code.isActive
      });
      toast.success(code.isActive ? 'Code deactivated' : 'Code activated');
      mutate();
    } catch (error) {
      toast.error('Failed to update code');
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (codeId) => {
    if (!confirm('Delete this discount code? This cannot be undone.')) return;
    try {
      await apiV2.delete(`/organizations/${orgId}/promo-codes/${codeId}`);
      toast.success('Code deleted');
      mutate();
    } catch (error) {
      toast.error('Failed to delete code');
    }
  };

  const copyToClipboard = (text, codeId) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(codeId);
    toast.success("Copied!");
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  if (isLoading) {
    return (
      <TabPageLayout title="Discounts">
        <PageLoader />
      </TabPageLayout>
    );
  }

  return (
    <TabPageLayout
      title="Discounts"
      subtitle="Create and track discount codes for your customers"
      actions={
        <Button 
          onClick={openAddModal}
          className="bg-blue-600 hover:bg-blue-700 text-white h-9"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Code
        </Button>
      }
    >
      <div className="space-y-4">
        {/* Codes List */}
        {codes.length === 0 ? (
          <EmptyState
            icon={<Tag />}
            title="No discount codes yet"
            description="Create discount codes to offer promotions to your customers"
            action={
              <Button 
                onClick={openAddModal}
                className="bg-blue-600 hover:bg-blue-700 text-white h-10"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create First Code
              </Button>
            }
          />
        ) : (
          <div className="space-y-3">
            {codes.map((code) => {
              // Use totalSavings from API if available, otherwise estimate
              const totalSaved = code.totalSavings || 0;
              
              return (
                <div key={code.id} className="bg-white rounded-lg shadow-[0_8px_24px_rgba(0,0,0,0.04)] p-4">
                  <div className="flex items-center justify-between gap-4">
                    {/* Left side - Code info */}
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                        code.discountType === 'percentage' ? 'bg-green-100' : 'bg-blue-100'
                      }`}>
                        {code.discountType === 'percentage' 
                          ? <Percent className="w-4 h-4 text-green-600" />
                          : <DollarSign className="w-4 h-4 text-blue-600" />
                        }
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-gray-900">{code.code}</span>
                          <button
                            onClick={() => copyToClipboard(code.code, code.id)}
                            className="p-1 hover:bg-gray-100 rounded"
                          >
                            {copiedCode === code.id ? (
                              <Check className="w-3.5 h-3.5 text-green-600" />
                            ) : (
                              <Copy className="w-3.5 h-3.5 text-gray-400" />
                            )}
                          </button>
                        </div>
                        <p className="text-sm text-gray-500">
                          {code.discountType === 'percentage' 
                            ? `${code.discountValue}% off`
                            : `$${code.discountValue} off`
                          }
                        </p>
                      </div>
                    </div>
                    
                    {/* Right side - Stats & Actions */}
                    <div className="flex items-center gap-4">
                      {/* Usage & Savings */}
                      <div className="flex items-center gap-3 text-sm text-gray-600">
                        <span>{code.currentUses || 0} uses</span>
                        <span className="text-green-600 font-medium">${totalSaved.toFixed(0)} saved</span>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-1">
                        <OnOffToggle
                          value={code.isActive}
                          onChange={() => handleToggleActive(code)}
                          disabled={togglingId === code.id}
                        />
                        <Button variant="ghost" size="sm" onClick={() => openEditModal(code)} className="h-8 w-8 p-0">
                          <Edit className="w-4 h-4 text-gray-500" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(code.id)}
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={(open) => { if (!open) { setShowModal(false); resetForm(); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingCode ? 'Edit' : 'Create'} Discount Code
            </DialogTitle>
            <DialogDescription>
              Discount codes give customers a percentage or fixed amount off their purchase.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Code <span className="text-red-500">*</span></Label>
              <Input
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="e.g., SAVE20"
                maxLength={20}
                disabled={!!editingCode}
                className="font-mono"
              />
              <p className="text-xs text-gray-500 mt-1">Customers enter this at checkout</p>
            </div>
            
            <div>
              <Label>Description (optional)</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="e.g., Summer sale discount"
                maxLength={100}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Discount Type</Label>
                <Select 
                  value={formData.discountType} 
                  onValueChange={(v) => setFormData({ ...formData, discountType: v })}
                >
                  <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Value <span className="text-red-500">*</span></Label>
                <Input
                  type="number"
                  value={formData.discountValue}
                  onChange={(e) => setFormData({ ...formData, discountValue: e.target.value })}
                  placeholder={formData.discountType === 'percentage' ? '20' : '10'}
                  min="0"
                  max={formData.discountType === 'percentage' ? '100' : undefined}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Max Uses (optional)</Label>
                <Input
                  type="number"
                  value={formData.maxUses}
                  onChange={(e) => setFormData({ ...formData, maxUses: e.target.value })}
                  placeholder="Unlimited"
                  min="1"
                />
              </div>
              <div>
                <Label>Expires in Days</Label>
                <Input
                  type="number"
                  value={formData.expiresInDays}
                  onChange={(e) => setFormData({ ...formData, expiresInDays: e.target.value })}
                  placeholder="Never"
                  min="1"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setShowModal(false); resetForm(); }} className="h-10">
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={saving}
              className="h-10 bg-blue-600 hover:bg-blue-700 text-white"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingCode ? 'Update' : 'Create'} Code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TabPageLayout>
  );
};

export default DiscountsTab;
